import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PCContainer from "../../components/atoms/layout/pcContainer";

import useDisplay from "../../hooks/useDisplay";
import { route } from "../../router/route";
import LSRgDisplay02 from "../../components/molecules/text/linSeedKr/Rg/lsRgDisplay02";
import LSRgH1HeadLine from "../../components/molecules/text/linSeedKr/Rg/lsRgH1HeadLine";
import Empty from "../../components/molecules/empty";
import Pagination from "../../components/molecules/pagination";
import ColumnItemPage from "../../components/organisms/column/columnItemPage";
import LSBdDisplay02 from "../../components/molecules/text/linSeedKr/Bd/lsBdDisplay02";
import LSBdDisplay01 from "../../components/molecules/text/linSeedKr/Bd/lsBdDisplay01";
import SelecteBoxValue from "../../components/organisms/selectbox/selecteBoxValue";
import { errorHandler } from "../../util/errorHandler";
import ColumnApi from "../../api/column.api";
import { fileUrl } from "../../api/api";
import StyledCategoryItem from "../../components/molecules/button/styledCategoryItem";
import Loading from "../../components/templates/loading";

const ColumnList = () => {
  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars

  const [categoryBoxOpen, setCategoryBoxOpen] = useState(false);
  const [categoryValue, setCategoryValue] = useState(-1);
  const [categoryList, setCategoryList] = useState([
    {
      title: "전체",
      value: -1,
    },
  ]);

  const [productList, setProductList] = useState([]);
  const [hotProductList, setHotProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHotLoading, setIsHotLoading] = useState(true);

  const page = useMemo(() => {
    const requestedPage = Number(searchParams.get("page"));
    return Number.isInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : 1;
  }, [searchParams]);

  const requestedCategoryValue = useMemo(() => {
    const value = Number(searchParams.get("categoryValue"));
    return Number.isInteger(value) && (value === -1 || value > 0) ? value : -1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 6, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  useEffect(() => {
    categoryListFunc();
    hotDataListFunc();
  }, []);

  useEffect(() => {
    setCategoryValue(requestedCategoryValue);

    dataListFunc(requestedCategoryValue);
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryListFunc = async () => {
    try {
      const columnCList = (
        await ColumnApi.getCategoryList({
          page: 1,
          size: 0,
          activation: "Active",
        })
      ).data.data.content;

      const categoryTemp = (columnCList ?? []).map((item) => {
        return {
          ...item,
          value: item.id,
        };
      });

      setCategoryList([{ title: "전체", value: -1 }, ...categoryTemp]);
    } catch (error) {
      errorHandler(error);
    }
  };

  const dataListFunc = async (selectedCategoryValue = -1) => {
    setIsLoading(true);
    try {
      const data = {
        page: page,
        size: pagination.size,
        activation: "Active",
      };

      if (selectedCategoryValue > 0) {
        data.columnCategoryId = selectedCategoryValue;
      }

      const response = (await ColumnApi.getList(data)).data.data;

      setProductList(response.content ?? []);
      setPagination((prev) => ({
        ...prev,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / prev.size),
      }));
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  const hotDataListFunc = async () => {
    setIsHotLoading(true);
    try {
      const data = {
        page: 1,
        size: 5,
        activation: "Active",
        sort: "viewCount",
        orderBy: "DESC",
      };

      const response = (await ColumnApi.getList(data)).data.data;

      setHotProductList(response.content ?? []);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsHotLoading(false);
    }
  };

  return (
    <>
      <ColumnListSection>
        <PCContainer
          style={{
            flexDirection: "column",
            padding: isMobile
              ? "40px 20px 80px 20px"
              : isTablet
                ? "40px 60px 80px 60px"
                : "80px 0px 150px 0px",
            width: "100%",
          }}
        >
          <LSRgDisplay02
            style={{
              color: "#000000",
              marginBottom: isMobile ? 16 : isTablet ? 30 : 30,
              fontSize: isMobile ? 16 : 24,
              lineHeight: isMobile ? "22.4px" : "33.6px",
            }}
          >
            📚 금융 칼럼
          </LSRgDisplay02>

          {isMobile ? (
            <SelecteBoxValue
              style={{
                marginBottom: 20,
              }}
              selectedValue={categoryValue}
              isOpen={categoryBoxOpen}
              wrapperHandler={() => {
                setCategoryBoxOpen(!categoryBoxOpen);
              }}
              itemList={categoryList}
              selectedValueSetterFunc={(value) => {
                setCategoryValue(value);
                navigate(`${route.column_list}?page=1&categoryValue=${value}`);
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: "16px",
                marginBottom: 60,
              }}
            >
              {categoryList.map((item) => {
                return (
                  <StyledCategoryItem
                    key={item.value}
                    onClick={() => {
                      navigate(
                        `${route.column_list}?page=1&categoryValue=${item.value}`,
                      );
                    }}
                    isActive={categoryValue === item.value}
                    title={item.title}
                  />
                );
              })}
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: isSmall ? "column" : "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                width: isSmall ? "100%" : 790,
              }}
            >
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: isMobile ? "19px" : "30px",
                    flexDirection: "row",
                    justifyContent: isSmall ? "space-between" : "flex-start",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  {isLoading ? (
                    <Loading />
                  ) : productList.length > 0 ? (
                    productList.map((item) => {
                      return (
                        <ColumnItemPage
                          key={item.id}
                          data={item}
                          categoryValue={categoryValue}
                        />
                      );
                    })
                  ) : (
                    <Empty title={"등록된 칼럼이 없습니다."} />
                  )}
                </div>
              </div>

              {!isLoading && productList.length > 0 ? (
                <Pagination
                  route={route.column_list}
                  queryString={`categoryValue=${categoryValue}`}
                  currentPage={Number(page)}
                  totalPages={pagination.totalPages}
                  rangeSize={pagination.rangeSize}
                />
              ) : null}
            </div>

            {!isHotLoading && hotProductList.length > 0 ? (
              <div
                style={{
                  marginTop: isSmall ? 60 : 0,
                  position: "sticky",
                  top: 200,
                  background: "#F6F6FF",
                  boxShadow: "0px 0px 6px 0px #0000001A",
                  borderRadius: 15,
                  width: isSmall ? "100%" : 380,
                  height: isSmall ? "auto" : 675,
                  padding: isMobile ? 20 : "30px 20px",
                  paddingBottom: isSmall ? 0 : 200,
                }}
              >
                <LSBdDisplay02
                  style={{
                    color: "#000000",
                    marginBottom: isMobile ? 19 : 26,
                    fontSize: isMobile ? 15 : 24,
                    lineHeight: isMobile ? "21px" : "33.6px",
                  }}
                >
                  🔥️ 조회수가 가장 많아요!
                </LSBdDisplay02>

                <ImageContainer isMobile={isMobile} isTablet={isTablet}>
                  <ClickableImage
                    onClick={() => {
                      navigate(
                        `${route.column_detail}/${hotProductList[0].id}`,
                      );
                    }}
                    src={
                      hotProductList[0].thumbnailServerFileNameColumn
                        ? `${fileUrl}/${hotProductList[0].thumbnailServerFileNameColumn}`
                        : "/assets/images/column_temp02.png"
                    }
                    alt="column_temp02"
                  />
                </ImageContainer>

                {hotProductList.map((item, index) => {
                  return (
                    <RightTextContain
                      onClick={() => {
                        navigate(`${route.column_detail}/${item.id}`);
                      }}
                      key={item.id}
                      style={{
                        height: isMobile ? 47 : 63,
                        borderBottom:
                          hotProductList.length === index + 1
                            ? 0
                            : "1px solid rgb(219, 221, 224)",
                      }}
                    >
                      <LSBdDisplay01
                        style={{
                          width: isMobile ? 20 : 32,
                          marginRight: 10,
                          color: "#5050FF",
                          fontSize: isMobile ? 14 : 20,
                          lineHeight: isMobile ? "19.6px" : "28px",
                        }}
                      >
                        0{index + 1}
                      </LSBdDisplay01>
                      <ProductTitle
                        style={{
                          color: "#000000",
                          fontSize: isMobile ? 14 : 20,
                          lineHeight: isMobile ? "19.6px" : "28px",
                        }}
                      >
                        {item.title}
                      </ProductTitle>
                    </RightTextContain>
                  );
                })}
              </div>
            ) : null}
          </div>
        </PCContainer>
      </ColumnListSection>
    </>
  );
};

export default ColumnList;

const ColumnListSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const RightTextContain = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  border-bottom: 1px solid rgb(219, 221, 224);

  cursor: pointer;
`;

const ProductTitle = styled(LSRgH1HeadLine)`
  text-overflow: ellipsis;
  overflow: hidden;
  word-break: break-word;

  display: -webkit-box;
  -webkit-line-clamp: 1; // 원하는 라인수
  -webkit-box-orient: vertical;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: ${({ isMobile, isTablet }) =>
    isMobile ? "214px" : isTablet ? "auto" : "240px"};
  border-radius: 15px;
  overflow: hidden;
`;

const ClickableImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 15px;
  cursor: pointer;
  object-fit: cover;
  transition:
    transform 0.3s ease,
    filter 0.3s ease;

  &:hover {
    transform: scale(1.04); /* 이미지 확대 */
    filter: brightness(1.1); /* 밝기 증가 */
  }
`;
